import { FC, useCallback, useMemo, useState } from "react";
import { AilenaxBox } from "../AilenaxBox/AilenaxBox";
import classNames from "classnames";
import { DonoLeaderboardEntry } from "@ailenax-overlay/shared";
import { useSocketEvent } from "@/hooks/useSocketEvent";
import { useSocketCallback } from "@/hooks/useSocketCallback";
import JsxParser, { type TProps } from 'react-jsx-parser'

import ailenaBanger2 from "../../assets/ailenaBanger2.png";

import classes from "./DonoLeaderboard.module.scss";

interface DonoLeaderboardProps {
    leaderboardTextLeft?: string;
    leaderboardTextRight?: string;
}

const availableComponents: TProps["components"] = {
    "ailenaBanger": () => <img alt="Banger" className="inline ml-0.5 max-w-none h-[0.9rem] relative top-[-2px]" src={ailenaBanger2}/>,
};

const getPointClass = (n: number) => {
    if (n >= 1000) {
        return classes["bits-100000"];
    } else if (n >= 750) {
        return classes["bits-10000"];
    } else if (n >= 500) {
        return classes["bits-5000"];
    } else if (n >= 250) {
        return classes["bits-1000"];
    } else if (n >= 100) {
        return classes["bits-100"];
    } 
    return "";
}

export const DonoLeaderboard: FC<DonoLeaderboardProps> = ({ leaderboardTextLeft, leaderboardTextRight }) => {
    const [shown, setShown] = useState(false);
    const [leaderboard, setLeaderboard] = useState<DonoLeaderboardEntry[]>([]);

    useSocketEvent("toggle-leaderboard", (e) => {
        setLeaderboard(
            e.leaderboard.map((e) => {
                if (e.user === "DriftyMcSlidey") {
                    e.user = "Anonymous";
                }

                return e;
            })
        );
        setShown(e.visible);
    });

    useSocketEvent("dono-leaderboard-update", (e) => {
        setLeaderboard(e.leaderboard.map((e) => {
                if (e.user === "DriftyMcSlidey") {
                    e.user = "Anonymous";
                }

                return e;
            }));
    });

    useSocketCallback("get-monthly-dono-leaderboard", (visible, lb) => {
        setShown(visible);
        if (JSON.stringify(leaderboard) !== JSON.stringify(lb)) {
            setLeaderboard(lb.map((e) => {
                if (e.user === "DriftyMcSlidey") {
                    e.user = "Anonymous";
                }

                return e;
            }));
        }
    });

    const left = useMemo(() => leaderboard.slice(0, 3), [leaderboard]);
    const right = useMemo(() => leaderboard.slice(3, 6), [leaderboard]);

    const getPlace = useCallback((n: number) => {
        if (n === 1) {
            return <img alt="" className="max-w-none h-[1.25rem]" src="https://assets.twitch.tv/assets/GiftBadge-Gold_36-1b402f71cfd08a9bcbaa.png"/>
        } else if (n === 2) {
            return <img alt="" className="max-w-none h-[1.25rem]" src="https://assets.twitch.tv/assets/GiftBadge-Silver_36-bb7c268e0452a2cdcc8d.png"/>
        } else if (n === 3) {
            return <img alt="" className="max-w-none h-[1.25rem]" src="https://assets.twitch.tv/assets/GiftBadge-Bronze_36-fd0ee2ef5196b3414a2f.png"/>
        }

        return <span>{ n }</span>
    }, []);

    const textLeft = useMemo(() => {
        if (!leaderboardTextLeft) {
            return null;
        }

        return <JsxParser jsx={leaderboardTextLeft.replaceAll("\n", "<br/>")} components={availableComponents} renderInWrapper={false} />
    }, [leaderboardTextLeft])

    const textRight = useMemo(() => {
        if (!leaderboardTextRight) {
            return null;
        }

        return <JsxParser jsx={leaderboardTextRight.replaceAll("\n", "<br/>")} components={availableComponents} renderInWrapper={false} />
    }, [leaderboardTextRight])

    return (
        <>
            { left.length > 0 && (
                <div className="absolute flex flex-col gap-2 w-[370px]" style={{ contain: "layout paint", willChange: "transform, opacity", transition: `transform 600ms linear ${shown ? "1000ms" : "600ms"}, opacity 600ms linear ${shown ? "1000ms" : "600ms"}`, transform: shown ? "translateX(-102%)" : "translateX(0)" , opacity: shown ? 1 : 0 }}>
                    <AilenaxBox className={classNames("leaderboard-left z-10 border-[5px]! flex flex-col max-h-[100px]")}>
                        { left.map((entry, i) => (
                            <div key={i} className="flex flex-row items-center justify-start gap-2 w-full">
                                { getPlace(leaderboard.indexOf(entry) + 1) }
                                <span className="flex-auto">{ entry.user }</span>
                                <span className={getPointClass(entry.amount)}>
                                    { entry.amount.toLocaleString() }
                                    <img alt="Banger" className="inline ml-0.5 max-w-none h-[0.9rem] relative top-[-2px]" src={ailenaBanger2}/>
                                </span>
                            </div>
                        ))}
                    </AilenaxBox>
                    { leaderboardTextLeft && (
                        <AilenaxBox className={classNames("leaderboad-box-left z-0 text-center")} style={{ borderWidth: "2px", padding: 0, contain: "layout paint", willChange: "transform", transition: `transform 600ms linear ${shown ? "1600ms" : ""}, opacity 600ms linear ${shown ? "1600ms" : ""}`, transform: shown ? "translateY(0)" : "translateY(-140%)", opacity: shown ? 1 : 0 }}>
                            <span className="text-[1.3rem]">{ textLeft }</span>
                        </AilenaxBox>
                    )}
                </div>
            )}
            { right.length > 0 && (
                <div className="absolute flex flex-col gap-2 w-[370px] right-0" style={{ contain: "layout paint", willChange: "transform, opacity", transition: `transform 600ms linear ${shown ? "1000ms" : "600ms"}, opacity 600ms linear ${shown ? "1000ms" : "600ms"}`, transform: shown ? "translateX(102%)" : "translateX(0)", opacity: shown ? 1 : 0 }}>
                    <AilenaxBox className={classNames("leaderboard-right z-10 border-[5px]! flex flex-col max-h-[100px]")}>
                    { right.map((entry, i) => (
                            <div key={i} className="flex flex-row items-center justify-start gap-2 w-full">
                                { getPlace(leaderboard.indexOf(entry) + 1) }
                                <span className="flex-auto">{ entry.user }</span>
                                <span className={getPointClass(entry.amount)}>
                                    { entry.amount.toLocaleString() }
                                    <img alt="Banger" className="inline ml-0.5 max-w-none h-[0.9rem] relative top-[-2px]" src={ailenaBanger2}/>
                                    </span>
                            </div>
                        ))}
                    </AilenaxBox>
                    { leaderboardTextRight && (
                        <AilenaxBox className={classNames("leaderboad-box-right z-0 text-center")} style={{ borderWidth: "2px", padding: 0, contain: "layout paint", willChange: "transform", transition: `transform 600ms linear ${shown ? "1600ms" : ""}, opacity 600ms linear ${shown ? "1600ms" : ""}`, transform: shown ? "translateY(0)" : "translateY(-140%)", opacity: shown ? 1 : 0 }}>
                            <span className="text-[1.3rem]">{ textRight }</span>
                        </AilenaxBox>
                    )}
                </div>
            )}
        </>
    );
}